import { FinalCut, RenderStatus } from 'services/finalcut.service';
import { FileStatus, FileType, ProjectFile } from 'services/file.service';
import { Folder } from 'services/folders.service';
import { Project } from 'services/project.service';
import { Story } from 'services/story.service';
import { Option } from 'components/UI/select/select';

export const UNFILED_FOLDER_ID = -1;
export const ALL_ACTIVE_FOLDER_ID = -3;
export const CUSTOM_FOLDER_ID = -2;

/* HEADERS */

export const PROJECTS_FILES_HEADERS = [
  'Thumb',
  'File Name',
  'Status',
  'Type',
  'Duration',
  'Size',
  'Resolution',
  'Frame Rate',
  'Bitrate',
  'Media Origin Date',
  'Date Created',
  'Date Accessed',
  'Date Updated',
] as const;

export const PROJECTS_FOLDERS_HEADERS = ['Name', 'Date Created', 'Date Updated'] as const;

export const PROJECTS_STORIES_HEADERS = ['Story', 'Duration', 'Date Created', 'Date Accessed', 'Date Updated'] as const;

export const PROJECTS_LIST_HEADERS = [
  'Project Name',
  'Status',
  'Size',
  'Date Created',
  'Date Accessed',
  'Date Updated',
] as const;

export const PROJECTS_FINALCUTS_HEADERS = [
  'Thumb',
  'Name',
  'Status',
  'Tier',
  'Duration',
  'Size',
  'Resolution',
  'Media Origin Date',
  'Date Created',
  'Date Accessed',
  'Date Updated',
] as const;

export const PROJECTS_HEADERS_SELECTS: Record<string, Index> = {
  folders: PROJECTS_FILES_HEADERS.findIndex((el) => el === 'Date Created'),
  files: PROJECTS_FILES_HEADERS.findIndex((el) => el === 'Date Created'),
  stories: PROJECTS_STORIES_HEADERS.findIndex((el) => el === 'Date Created'),
  projects: PROJECTS_LIST_HEADERS.findIndex((el) => el === 'Date Created'),
  finalcuts: PROJECTS_FINALCUTS_HEADERS.findIndex((el) => el === 'Media Origin Date'),
};

export const TIME_ZONE_OPTIONS: Option[] = Array.from({ length: 25 }, (_, i) => {
  const offset = i - 12;
  const sign = offset >= 0 ? '+' : '';
  return {
    label: `UTC ${sign}${offset}:00`,
    value: offset,
  };
});

/* SORTING */

type Val = string | number | Date;

export const getFileSortCb = (sortBy: (typeof PROJECTS_FILES_HEADERS)[Index]): ((data: ProjectFile) => Val) => {
  switch (sortBy) {
    case 'Status':
      return (data: ProjectFile) => FileStatus[data.status];
    case 'Type':
      return (data: ProjectFile) => FileType[data.fileType];
    case 'Duration':
      return (data: ProjectFile) => data.duration ?? 0;
    case 'Size':
      return (data: ProjectFile) => data.size ?? 0;
    case 'Resolution':
      return (data: ProjectFile) => data.width ?? 0;
    case 'Frame Rate':
      return (data: ProjectFile) => data.frameRate ?? 0;
    case 'Bitrate':
      return (data: ProjectFile) => data.bitRate ?? data.origBitRate ?? 0;
    case 'Date Created':
      return (data: ProjectFile) => new Date(data.createdAt);
    case 'Date Accessed':
      return (data: ProjectFile) => new Date(data.accessedAt ?? 0);
    case 'Date Updated':
      return (data: ProjectFile) => new Date(data.updatedAt);
    case 'Media Origin Date':
      return (data: ProjectFile) => new Date(data.mediaOriginDate ?? '');
    default:
      return (data: ProjectFile) => data.fileName;
  }
};

export const getStoriesSortCb = (sortBy: (typeof PROJECTS_STORIES_HEADERS)[Index]): ((data: Story) => Val) => {
  switch (sortBy) {
    case 'Date Created':
      return (data: Story) => new Date(data.createdAt);
    case 'Date Accessed':
      return (data: Story) => new Date(data.accessedAt ?? 0);
    case 'Date Updated':
      return (data: Story) => new Date(data.updatedAt ?? 0);
    default:
      return (data: Story) => data.name;
  }
};

export const getFoldersSortCb = (sortBy: (typeof PROJECTS_FOLDERS_HEADERS)[Index]): ((data: Folder) => Val) => {
  switch (sortBy) {
    case 'Name':
      return (data: Folder) => data.name;
    default:
      return (data: Folder) => data.name;
  }
};

export const getProjectsSortCb = (sortBy: (typeof PROJECTS_LIST_HEADERS)[Index]): ((data: Project) => Val) => {
  switch (sortBy) {
    case 'Status':
      return (data: Project) => FileStatus[data.status];
    case 'Size':
      return (data: Project) => data.size;
    case 'Date Created':
      return (data: Project) => new Date(data.createdAt);
    case 'Date Accessed':
      return (data: Project) => new Date(data.accessedAt ?? 0);
    case 'Date Updated':
      return (data: Project) => new Date(data.updatedAt ?? 0);
    default:
      return (data: Project) => data.name;
  }
};

export const getFinalCutsSortCb = (sortBy: (typeof PROJECTS_FINALCUTS_HEADERS)[Index]): ((data: FinalCut) => Val) => {
  switch (sortBy) {
    case 'Status':
      return (data: FinalCut) => RenderStatus[data.status];
    case 'Tier':
      return (data: FinalCut) => data.payment?.price?.name ?? data.subscription?.price?.name ?? '';
    case 'Duration':
      return (data: FinalCut) => new Date(data.duration ?? 0);
    case 'Date Created':
      return (data: FinalCut) => new Date(data.createdAt);
    case 'Date Accessed':
      return (data: FinalCut) => new Date(data.accessedAt ?? 0);
    case 'Date Updated':
      return (data: FinalCut) => new Date(data.updatedAt ?? 0);
    case 'Size':
      return (data: FinalCut) => data.size;
    case 'Media Origin Date':
      return (data: FinalCut) => new Date(data.mediaOriginDate ?? 0);
    default:
      return (data: FinalCut) => data.name;
  }
};
